import { useEffect, useState } from 'react';
import { getPackagesListingConfig, updatePackagesListingConfig } from '../../api/adminPackages';
import Button from '../../components/shared/ui/Button';

const DEFAULT_LABELS = {
  enquire: 'Enquire now',
  clearFilters: 'Clear filters',
  clearAll: 'Clear all',
  budget: 'Budget',
  travellers: 'Travellers',
  perPerson: 'per person',
  viewDetails: 'View details',
  resultsLabel: 'Showing',
  filtersLabel: 'Filters',
  applyFilters: 'Apply filters',
  resetFilters: 'Reset',
};

const parseLabels = (value) => {
  if (!value) return { ...DEFAULT_LABELS };
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object') {
        return { ...DEFAULT_LABELS, ...parsed };
      }
    } catch (_) {
      return { ...DEFAULT_LABELS };
    }
  }
  if (typeof value === 'object') return { ...DEFAULT_LABELS, ...value };
  return { ...DEFAULT_LABELS };
};

const AdminPackageListing = () => {
  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    bannerTitle: '',
    bannerSubtitle: '',
    bannerImageUrl: '',
    defaultSort: 'popularity',
    showTrendingRow: true,
    showThemeChips: true,
    resultsMode: 'pagination',
    pageSize: 12,
    cardStyle: 'compact-premium',
    filterVisibility: {
      destination: true,
      budget: true,
      duration: true,
      theme: true,
      rating: true,
      travellers: true,
      search: true,
    },
    showQuickBadges: true,
    showEnquiryCta: true,
    showReviewsCount: true,
    seoTitle: '',
    seoDescription: '',
    seoImage: '',
    labels: { ...DEFAULT_LABELS },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const res = await getPackagesListingConfig();
      const data = res?.data || res || {};
      setForm({
        title: data.title || data.heading || '',
        subtitle: data.subtitle || '',
        bannerTitle: data.banner_title || data.bannerTitle || data.title || '',
        bannerSubtitle: data.banner_subtitle || data.bannerSubtitle || data.subtitle || '',
        bannerImageUrl: data.banner_image_url || data.bannerImageUrl || '',
        defaultSort: data.default_sort || data.defaultSort || 'popularity',
        showTrendingRow: data.show_trending_row ?? data.showTrendingRow ?? true,
        showThemeChips: data.show_theme_chips ?? data.showThemeChips ?? true,
        resultsMode: data.results_mode || data.resultsMode || 'pagination',
        pageSize: Number(data.page_size || data.pageSize || 12),
        cardStyle: data.card_style || data.cardStyle || 'compact-premium',
        filterVisibility: {
          destination: data.filter_visibility?.destination ?? data.filterVisibility?.destination ?? true,
          budget: data.filter_visibility?.budget ?? data.filterVisibility?.budget ?? true,
          duration: data.filter_visibility?.duration ?? data.filterVisibility?.duration ?? true,
          theme: data.filter_visibility?.theme ?? data.filterVisibility?.theme ?? true,
          rating: data.filter_visibility?.rating ?? data.filterVisibility?.rating ?? true,
          travellers: data.filter_visibility?.travellers ?? data.filterVisibility?.travellers ?? true,
          search: data.filter_visibility?.search ?? data.filterVisibility?.search ?? true,
        },
        showQuickBadges: data.show_quick_badges ?? data.showQuickBadges ?? true,
        showEnquiryCta: data.show_enquiry_cta ?? data.showEnquiryCta ?? true,
        showReviewsCount: data.show_reviews_count ?? data.showReviewsCount ?? true,
        seoTitle: data.seo_title || data.seoTitle || '',
        seoDescription: data.seo_description || data.seoDescription || '',
        seoImage: data.seo_image || data.seoImage || '',
        labels: parseLabels(data.labels),
      });
    } catch (err) {
      setError(err.message || 'Failed to load listing settings');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateLabel = (key, value) => {
    setForm((prev) => ({ ...prev, labels: { ...prev.labels, [key]: value } }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await updatePackagesListingConfig({
        title: form.title,
        subtitle: form.subtitle,
        banner_title: form.bannerTitle,
        banner_subtitle: form.bannerSubtitle,
        banner_image_url: form.bannerImageUrl,
        default_sort: form.defaultSort,
        show_trending_row: form.showTrendingRow,
        show_theme_chips: form.showThemeChips,
        results_mode: form.resultsMode,
        page_size: form.pageSize,
        card_style: form.cardStyle,
        filter_visibility: form.filterVisibility,
        show_quick_badges: form.showQuickBadges,
        show_enquiry_cta: form.showEnquiryCta,
        show_reviews_count: form.showReviewsCount,
        seo_title: form.seoTitle,
        seo_description: form.seoDescription,
        seo_image: form.seoImage,
        labels: form.labels,
      });
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Tours & Packages</p>
          <h1 className="text-xl font-semibold text-slate-900">Packages Listing Settings</h1>
        </div>
        <Button onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save settings'}
        </Button>
      </div>
      {error && <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}

      <div className="rounded-3xl border border-slate-200 bg-white p-5 space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Basics</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <input
            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Listing title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          />
          <input
            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Subtitle"
            value={form.subtitle}
            onChange={(e) => setForm((prev) => ({ ...prev, subtitle: e.target.value }))}
          />
          <input
            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Banner title"
            value={form.bannerTitle}
            onChange={(e) => setForm((prev) => ({ ...prev, bannerTitle: e.target.value }))}
          />
          <input
            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Banner subtitle"
            value={form.bannerSubtitle}
            onChange={(e) => setForm((prev) => ({ ...prev, bannerSubtitle: e.target.value }))}
          />
          <input
            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm md:col-span-2"
            placeholder="Banner image URL"
            value={form.bannerImageUrl}
            onChange={(e) => setForm((prev) => ({ ...prev, bannerImageUrl: e.target.value }))}
          />
          <select
            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            value={form.defaultSort}
            onChange={(e) => setForm((prev) => ({ ...prev, defaultSort: e.target.value }))}
          >
            <option value="popularity">Popularity (Trending)</option>
            <option value="topRated">Top rated</option>
            <option value="priceAsc">Price low-high</option>
            <option value="priceDesc">Price high-low</option>
            <option value="durationAsc">Shortest duration</option>
            <option value="newest">Newest</option>
            <option value="recommended">Recommended</option>
          </select>
          <select
            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            value={form.resultsMode}
            onChange={(e) => setForm((prev) => ({ ...prev, resultsMode: e.target.value }))}
          >
            <option value="pagination">Pagination</option>
            <option value="loadmore">Load more</option>
          </select>
          <input
            type="number"
            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Page size"
            value={form.pageSize}
            onChange={(e) => setForm((prev) => ({ ...prev, pageSize: Number(e.target.value || 12) }))}
          />
          <input
            className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Card style"
            value={form.cardStyle}
            onChange={(e) => setForm((prev) => ({ ...prev, cardStyle: e.target.value }))}
          />
        </div>
        <div className="flex flex-wrap gap-6 text-xs">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.showTrendingRow}
              onChange={(e) => setForm((prev) => ({ ...prev, showTrendingRow: e.target.checked }))}
            />
            Show trending row
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.showThemeChips}
              onChange={(e) => setForm((prev) => ({ ...prev, showThemeChips: e.target.checked }))}
            />
            Show theme chips
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.showQuickBadges}
              onChange={(e) => setForm((prev) => ({ ...prev, showQuickBadges: e.target.checked }))}
            />
            Show quick badges
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.showEnquiryCta}
              onChange={(e) => setForm((prev) => ({ ...prev, showEnquiryCta: e.target.checked }))}
            />
            Show enquiry CTA
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.showReviewsCount}
              onChange={(e) => setForm((prev) => ({ ...prev, showReviewsCount: e.target.checked }))}
            />
            Show review count
          </label>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-900">Filter visibility</h2>
        <div className="grid gap-3 md:grid-cols-3">
          {Object.entries(form.filterVisibility).map(([key, value]) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={value}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    filterVisibility: { ...prev.filterVisibility, [key]: e.target.checked },
                  }))
                }
              />
              {key}
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-900">SEO</h2>
        <input
          className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="SEO title"
          value={form.seoTitle}
          onChange={(e) => setForm((prev) => ({ ...prev, seoTitle: e.target.value }))}
        />
        <textarea
          className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
          rows={3}
          placeholder="SEO description"
          value={form.seoDescription}
          onChange={(e) => setForm((prev) => ({ ...prev, seoDescription: e.target.value }))}
        />
        <input
          className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="SEO image URL (optional)"
          value={form.seoImage}
          onChange={(e) => setForm((prev) => ({ ...prev, seoImage: e.target.value }))}
        />
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-900">Label overrides</h2>
        <div className="grid gap-3 md:grid-cols-2">
          {Object.entries(form.labels).map(([key, value]) => (
            <input
              key={key}
              className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
              placeholder={key}
              value={value}
              onChange={(e) => updateLabel(key, e.target.value)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPackageListing;
